import { Button } from "@svea/design-system-components-react";
import {
	ButtonGroup,
	FormWithTracking,
	isRequired,
	ReactSelectField,
	SimpleGrid,
	TextField,
	useText,
	validateCardPhoneNumber,
	validateEmail,
} from "@sveabank/cc-common";
import { usePageContext } from "../../hooks/context";
import type { ApplicantContactInformationStep, ConfigureCardState } from "../../types/step";
import { stepIs } from "../../utils/application";
import { useApplicationSettings } from "./hooks";

export type CardContactFormValues = {
	companyCountryCode: string;
	companyPhone: string;
	companyEmail: string;
	companyAddress: string;
	countryCode: string;
};

type Props = {
	address?: string;
	ssn?: string;
	card: ConfigureCardState;
	handleOnSubmit: (values: CardContactFormValues) => Promise<void>;
};

const CardContactForm = ({ address, card, ssn, handleOnSubmit }: Props) => {
	const [t, texts] = useText();
	const { countries, steps, applicationDetails } = usePageContext(useApplicationSettings);

	const applicantContactInformationStep = steps?.find(
		stepIs("ApplicantContactInformationStep"),
	) as ApplicantContactInformationStep;

	const applicantContactInformation = applicantContactInformationStep.state;
	const isApplicant =
		ssn === applicationDetails?.applicant?.nationalId && applicantContactInformation;

	return (
		<FormWithTracking<CardContactFormValues>
			onSubmit={handleOnSubmit}
			initialValues={
				ssn === card.nationalId
					? {
							companyAddress: address,
							companyCountryCode: card.phone?.countryCode,
							companyPhone: card.phone?.number,
							companyEmail: card.email,
						}
					: {
							companyAddress: address,
							companyCountryCode: isApplicant
								? applicantContactInformation.phone.countryCode
								: "+46",
							companyPhone: isApplicant ? applicantContactInformation.phone.number : undefined,
							companyEmail: isApplicant ? applicantContactInformation.email : undefined,
						}
			}
			matomoInstance={window.Matomo}
			render={({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit} name="cardContactForm">
					<SimpleGrid cols={2}>
						<ReactSelectField
							options={countries}
							label={t(texts.contactInfo.companyContact.countryCode)}
							ariaLabel={t(texts.contactInfo.companyContact.countryCode)}
							initialValue={card.phone?.countryCode || "+46"}
							name="companyCountryCode"
							validators={[isRequired]}
							floatingLabel
						/>
						<TextField
							label={t(texts.contactInfo.companyContact.phoneNumber)}
							type="tel"
							name="companyPhone"
							validators={[isRequired, validateCardPhoneNumber]}
						/>
						<TextField
							label={t(texts.common.email)}
							type="email"
							name="companyEmail"
							validators={[isRequired, validateEmail]}
						/>
						<TextField
							disabled
							label={t(texts.applicationSettings.cardSettings.address)}
							type="text"
							name="companyAddress"
							validators={[isRequired]}
							helperText={t(texts.applicationSettings.cardSettings.pinCodeInfo)}
						/>
					</SimpleGrid>
					<ButtonGroup row>
						<Button type="submit" isPending={submitting}>
							{t(texts.applicationSettings.buttonText.save)}
						</Button>
					</ButtonGroup>
				</form>
			)}
		/>
	);
};

export default CardContactForm;
